import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { fetcher } from '@dashboard/services/request';
import { pageKey } from '@dashboard/lib/queryKeys';
import { Form, FormSubmit, TextInput } from '@dashboard/components/Formik';
import { formatPriceForInput, formatPriceInput } from '@dashboard/services/formatting/price';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import { ImageField } from '@dashboard/components/Formik/components/Image';
import { useTicketGroups } from '../TicketGroupsContext';
import { fromZonedTime } from 'date-fns-tz';
import { formatDateForInput } from '@/dashboard/services/formatting/date';
import handleAvailabilityBehaviorOnChange from '../handleAvailabilityBehaviorOnChange';
import {
  TicketTypeAvailabilityBehavior,
  TicketTypeAvailabilityStatus,
} from '@/dashboard/enums/tickets';
import * as Yup from 'yup';

export default function TicketTypeEdit({
  ticketType,
  ticketGroupId,
  cancelButton,
  submitButton,
  formSubmitDirection,
}) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();
  const { setHasUnsavedChanges, setIsAddingTicketType, setEditingTicketTypeId } =
    useTicketGroupEdit();
  const { onCancelEditTicketType } = useTicketTypeHelpers();
  const { onDirty } = useTicketTypeHelpers(ticketType?._id);
  const [shouldAddAnother, setShouldAddAnother] = useState(false);

  const { state: ticketGroups, dispatch } = useTicketGroups();
  const ticketGroup = ticketGroups.find(tg => tg._id === ticketGroupId);

  const defaultAvailabilityBehavior = page?.eventDetails?.startDate
    ? TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS
    : TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;

  const getSaleStartOnFromReference = triggerTicketType => {
    if (triggerTicketType == null || page?.eventDetails?.timezone == null) return null;

    const referencedTicketType = ticketGroups
      .flatMap(group => group.ticketTypes)
      .find(tt => tt._id === triggerTicketType);

    if (
      referencedTicketType?.availabilityBehavior ===
      TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS
    ) {
      if (page?.eventDetails?.startDate) {
        return formatDateForInput(page.eventDetails.startDate, page?.eventDetails?.timezone);
      }
      return null;
    }

    if (referencedTicketType?.saleEndOn) {
      return formatDateForInput(referencedTicketType.saleEndOn, page?.eventDetails?.timezone);
    }

    return null;
  };

  const editTicketTypeMutation = useMutation({
    mutationFn: values =>
      fetcher(
        ticketType?._id
          ? `/sellers/${sellerId}/ticket-types/${ticketType?._id}`
          : `/sellers/${sellerId}/ticket-types`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: values => {
      if (ticketType?._id) {
        dispatch({
          type: 'updateTicketType',
          ticketGroupId,
          ticketTypeId: ticketType._id,
          values,
        });
      } else {
        dispatch({ type: 'addTicketType', ticketGroupId, values });
      }

      setIsAddingTicketType(false);

      // delay resetting form to ensure isAddingTicketType is false
      window.setTimeout(() => {
        setHasUnsavedChanges(false);
      }, 250);

      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    },
  });

  return (
    <Form
      onDirty={onDirty}
      validationSchema={Yup.object().shape({
        triggerTicketType: Yup.string().when('availabilityBehavior', {
          is: value =>
            [
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
            ].includes(value),
          then: Yup.string().nullable().required('Please select a ticket'),
          otherwise: Yup.string().nullable(),
        }),
        saleStartOn: Yup.string().when('availabilityBehavior', {
          is: TicketTypeAvailabilityBehavior.DATE_TIME_BASED,
          then: Yup.string()
            .nullable()
            .test('date-required', 'A start or end date is required.', function (value) {
              const { saleEndOn } = this.parent;
              // Only show error on saleStartOn if both are empty
              if ((!value || value === '') && (!saleEndOn || saleEndOn === '')) {
                return false;
              }
              return true;
            }),
          otherwise: Yup.string().nullable(),
        }),
        saleEndOn: Yup.string().when('availabilityBehavior', {
          is: TicketTypeAvailabilityBehavior.DATE_TIME_BASED,
          then: Yup.string().nullable(),
          otherwise: Yup.string().nullable(),
        }),
      })}
      initialValues={{
        availabilityStatus:
          ticketType?.availabilityStatus || TicketTypeAvailabilityStatus.AVAILABLE,
        image: ticketType?.image,
        name: ticketType?.name || '',
        description: ticketType?.description || '',
        capacity: ticketType?.capacity === null ? null : ticketType?.capacity,
        pricing: ticketType?.pricing || 'paid',
        price: ticketType?.price
          ? formatPriceForInput({
              amount: ticketType.price,
              currency: page.eventDetails?.currency,
              style: 'decimal',
            })
          : null,
        discountedFromPrice: ticketType?.discountedFromPrice
          ? formatPriceForInput({
              amount: ticketType.discountedFromPrice,
              currency: page.eventDetails?.currency,
              style: 'decimal',
            })
          : null,
        bookingFee: {
          enabled: ticketType?.bookingFee?.enabled || false,
          type: ticketType?.bookingFee?.type || 'fixed',
          percentageFee: ticketType?.bookingFee?.percentageFee || 0,
          fixedFee: ticketType?.bookingFee?.fixedFee || 0,
        },
        minQuantity: ticketType?.minQuantity || 0,
        maxQuantity: ticketType?.maxQuantity || 10,
        showAvailableQuantity: ticketType?.showAvailableQuantity === false ? false : true,
        showTicketSaleDates: ticketType?.showTicketSaleDates === false ? false : true,
        hidden: ticketType?.hidden || false,
        hideWhenSoldOut: ticketType?.hideWhenSoldOut || false,
        hideWhenNotOnSale: ticketType?.hideWhenNotOnSale || false,
        hideWhenScheduled: ticketType?.hideWhenScheduled || false,
        hideWhenUnavailable: ticketType?.hideWhenUnavailable || false,
        reference: ticketType?.reference || '',
        saleStartOn: formatDateForInput(ticketType?.saleStartOn, page?.eventDetails?.timezone),
        saleEndOn:
          ticketType != null
            ? formatDateForInput(ticketType?.saleEndOn, page?.eventDetails?.timezone)
            : formatDateForInput(
                ticketType?.saleEndOn ||
                  (ticketType?.availabilityBehavior ===
                    TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS ||
                  defaultAvailabilityBehavior === TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS
                    ? page.eventDetails?.startDate
                    : null),
                page?.eventDetails?.timezone,
              ),
        triggerTicketType: ticketType?.triggerTicketType,
        availabilityBehavior: ticketType?.availabilityBehavior || defaultAvailabilityBehavior,
      }}
      onSubmit={async (values, actions) => {
        try {
          let price = formatPriceInput(values.price, page.eventDetails?.currency);
          let discountedFromPrice = values.discountedFromPrice
            ? formatPriceInput(values.discountedFromPrice, page.eventDetails?.currency)
            : null;

          if (values.pricing === 'free') {
            price = 0;
            discountedFromPrice = null;
          } else if (values.pricing === 'paid') {
            // validate price is greater than 0
            if (price <= 0) {
              return {
                error: 'Price is required for paid ticket types',
              };
            }
          }

          if (!values.minQuantity) {
            values.minQuantity = 0;
          }

          if (!values.maxQuantity) {
            values.maxQuantity = 10;
          }

          if (
            values.availabilityBehavior === TicketTypeAvailabilityBehavior.DATE_TIME_BASED &&
            (values.saleStartOn == null || values.saleStartOn === '') &&
            (values.saleEndOn == null || values.saleEndOn === '')
          ) {
            values.saleEndOn = null;
            values.saleStartOn = null;
            values.availabilityBehavior = TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;
          }

          if (values.triggerTicketType == '') {
            values.triggerTicketType = null;
          }

          if (
            [
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
              TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
            ].includes(values.availabilityBehavior) &&
            (values.triggerTicketType == null || values.triggerTicketType === '')
          ) {
            values.availabilityBehavior = TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;
          }

          const data = await editTicketTypeMutation.mutateAsync({
            ...values,
            saleStartOn: values.saleStartOn
              ? fromZonedTime(values.saleStartOn, page?.eventDetails?.timezone)
              : null,
            saleEndOn: values.saleEndOn
              ? fromZonedTime(values.saleEndOn, page?.eventDetails?.timezone)
              : null,
            triggerTicketType: values.triggerTicketType === '' ? null : values.triggerTicketType,
            price,
            discountedFromPrice,
            pageId: page._id,
            ticketGroupId,
          });

          if (!ticketType?._id) {
            // if new ticket type, reset form
            actions.resetForm();
          } else {
            // if editing, reset form with new values
            actions.resetForm({
              values: {
                availabilityStatus: data.availabilityStatus,
                image: data.image,
                name: data.name,
                description: data.description,
                capacity: data.capacity,
                pricing: data.pricing,
                price:
                  data.pricing === 'paid'
                    ? formatPriceForInput({
                        amount: data.price,
                        currency: page.eventDetails?.currency,
                        style: 'decimal',
                      })
                    : '',
                discountedFromPrice:
                  data.pricing === 'paid' && data.discountedFromPrice
                    ? formatPriceForInput({
                        amount: data.discountedFromPrice,
                        currency: page.eventDetails?.currency,
                        style: 'decimal',
                      })
                    : '',
                bookingFee: {
                  enabled: data.bookingFee?.enabled,
                  type: data.bookingFee?.type,
                  percentageFee: data.bookingFee?.percentageFee,
                  fixedFee: data.bookingFee?.fixedFee,
                },
                minQuantity: data.minQuantity,
                maxQuantity: data.maxQuantity,
                showAvailableQuantity: data.showAvailableQuantity,
                showTicketSaleDates: data.showTicketSaleDates,
                hidden: data.hidden,
                hideWhenSoldOut: data.hideWhenSoldOut,
                hideWhenNotOnSale: data.hideWhenNotOnSale,
                hideWhenScheduled: data.hideWhenScheduled,
                hideWhenUnavailable: data.hideWhenUnavailable,
                reference: data.reference,
                saleStartOn: formatDateForInput(data?.saleStartOn, page?.eventDetails?.timezone),
                saleEndOn: formatDateForInput(data?.saleEndOn, page?.eventDetails?.timezone),
                triggerTicketType: data?.triggerTicketType,
                availabilityBehavior: data?.availabilityBehavior || defaultAvailabilityBehavior,
              },
            });
          }

          return {
            data: data,
            onSuccess: () => {
              // if editing ticket type
              if (!ticketType?._id) {
                if (shouldAddAnother) {
                  setShouldAddAnother(false);
                  setEditingTicketTypeId(null);
                  // wait for next tick to set isAddingTicketType
                  // so that the form is reset and auto focused
                  window.setTimeout(() => {
                    setIsAddingTicketType(true);
                  }, 0);
                }
              }
            },
          };
        } catch (error) {
          return {
            error: error,
          };
        }
      }}
      inputClassName="text-sm"
      fields={({ values, setFieldValue }) => {
        const availableTicketTypeOptions = ticketGroups.flatMap(group =>
          group.ticketTypes
            .filter(tt => tt._id !== ticketType?._id)
            .map(tt => ({
              label: tt.name,
              value: tt._id,
            })),
        );

        const isCapacityReached =
          (values.capacity != null &&
            values.capacity != '' &&
            ticketType?.quantitySold != null &&
            ticketType?.quantitySold >= values.capacity) ||
          values.capacity === 0;

        return [
          {
            type: 'custom',
            renderField: () => {
              return (
                <div className="flex gap-x-2 relative">
                  <div className="w-[63px] h-[63px]">
                    <ImageField
                      id="image"
                      name="image"
                      image={values.image}
                      previewSize="variant_option"
                      variant="minimal"
                    />
                  </div>

                  <div className="flex-1">
                    <TextInput
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Name"
                      className="rounded-b-none focus:z-10 !py-[6px] relative text-sm"
                      required
                      border
                      autoFocus={!values.name}
                    />

                    <TextInput
                      id="description"
                      name="description"
                      className="border-t-0 rounded-t-none -mt-px focus:z-10 !py-[6px] relative text-sm"
                      type="text"
                      placeholder="Short description"
                      border
                    />
                  </div>
                </div>
              );
            },
            reduceMargin: true,
          },
          {
            type: 'columns',
            columns: 1,
            className: '!gap-y-0',
            gap: 'sm',
            fields: [
              {
                name: 'pricing',
                label: 'Pricing',
                type: 'radioCard',
                options: [
                  { label: 'Paid', value: 'paid' },
                  { label: 'Free', value: 'free' },
                ],
                columns: 2,
                gap: 'sm',
                reduceMargin: true,
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'price',
                    label: 'Price',
                    type: 'number',
                    required: values.pricing === 'paid',
                    reduceMargin: true,
                  },
                  {
                    name: 'discountedFromPrice',
                    label: 'Discounted from',
                    type: 'number',
                    reduceMargin: true,
                  },
                ],
                visible: values.pricing === 'paid',
                noMargin: true,
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'availabilityStatus',
                    type: 'select',
                    label: 'Availability',
                    tooltip:
                      isCapacityReached &&
                      `Disabled when tickets sold (currently ${ticketType?.quantitySold ?? 0} sold) exceeds ticket availability. Increase availability to change ticket availability status.`,
                    options: [
                      {
                        label: 'Select a ticket availability',
                        value: '',
                        disabled: true,
                      },
                      {
                        label: 'On sale',
                        value: TicketTypeAvailabilityStatus.AVAILABLE,
                        disabled: isCapacityReached,
                      },
                      {
                        label: 'Unavailable',
                        value: TicketTypeAvailabilityStatus.UNAVAILABLE,
                        disabled: isCapacityReached,
                      },
                      {
                        label: 'Sold out',
                        value: TicketTypeAvailabilityStatus.SOLD_OUT,
                      },
                    ],
                    reduceMargin: true,
                  },
                  {
                    name: 'capacity',
                    label: 'Quantity available',
                    placeholder: 'Quantity available',
                    type: 'number',
                    reduceMargin: true,
                    onChange: e => {
                      setFieldValue('capacity', e.target?.value);
                      const capacity = parseInt(e.target?.value, 10);

                      if (
                        capacity === 0 ||
                        (!isNaN(capacity) &&
                          ticketType?.quantitySold != null &&
                          ticketType?.quantitySold >= e.target?.value)
                      ) {
                        setFieldValue('availabilityStatus', TicketTypeAvailabilityStatus.SOLD_OUT);
                      }
                    },
                  },
                ],
              },
              {
                name: 'availabilityBehavior',
                label: 'Available from',
                type: 'select',
                options: [
                  {
                    label: 'Now until event starts',
                    value: TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS,
                  },
                  {
                    label: 'Always',
                    value: TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE,
                  },
                  {
                    label: 'Date & time',
                    value: TicketTypeAvailabilityBehavior.DATE_TIME_BASED,
                  },
                  {
                    label: 'When another ticket sale ends',
                    value: TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                  },
                  {
                    label: 'When another ticket sells out',
                    value: TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                  },
                  {
                    label: 'When another ticket sells out or sale ends',
                    value: TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                  },
                ],
                reduceMargin: true,
                onChange: e => {
                  handleAvailabilityBehaviorOnChange({
                    triggerKey: 'triggerTicketType',
                    value: e.target?.value,
                    setFieldValue,
                    page,
                    values,
                    getSaleStartOnFromReference,
                  });
                },
              },
              {
                name: 'triggerTicketType',
                type: 'select',
                label: 'Ticket',
                options: [
                  {
                    label: 'Select a ticket',
                    value: '',
                  },
                  ...availableTicketTypeOptions,
                ],
                visible: values =>
                  [
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                  ].includes(values.availabilityBehavior),
                reduceMargin: true,
                onChange: e => {
                  if (e.target?.value) {
                    const saleStartOn = getSaleStartOnFromReference(e.target?.value);
                    if (saleStartOn) {
                      setFieldValue('saleStartOn', saleStartOn);
                    } else {
                      setFieldValue('saleStartOn', '');
                    }
                  }

                  if (e.target?.value == '') {
                    setFieldValue('saleStartOn', '');
                    setFieldValue('saleEndOn', '');
                  }

                  setFieldValue('triggerTicketType', e.target.value);
                },
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'saleStartOn',
                    label: 'Start sale on',
                    placeholder: 'Start sale on',
                    type: 'datetime-local',
                    reduceMargin: true,
                    disabled: [
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                    ].includes(values.availabilityBehavior),
                  },
                  {
                    name: 'saleEndOn',
                    label: 'End sale on',
                    placeholder: 'End sale on',
                    type: 'datetime-local',
                    reduceMargin: true,
                  },
                ],
                visible: values => {
                  if (
                    values.availabilityBehavior === TicketTypeAvailabilityBehavior.DATE_TIME_BASED
                  ) {
                    return true;
                  }

                  return (
                    [
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                      TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                    ].includes(values.availabilityBehavior) &&
                    (values.triggerTicketType != null || values.triggerTicketType != '')
                  );
                },
              },
              {
                name: 'saleEndOn',
                label: 'End sale on',
                placeholder: 'End sale on',
                type: 'datetime-local',
                reduceMargin: true,
                visible: values =>
                  values.availabilityBehavior ===
                    TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT &&
                  (values.triggerTicketType != null || values.triggerTicketType != ''),
              },
              {
                type: 'fieldgroup',
                label: 'Visibility',
                fields: [
                  {
                    label: 'Hide ticket',
                    name: 'hidden',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket. You can show hidden tickets using query parameters',
                  },
                  {
                    label: 'Hide when scheduled',
                    name: 'hideWhenScheduled',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket when it is scheduled to go on sale',
                  },
                  {
                    label: 'Hide when not on sale',
                    name: 'hideWhenNotOnSale',
                    type: 'checkbox',
                    tooltip:
                      'Hide this ticket outside its sales dates (before start or after end date)',
                  },
                  {
                    label: 'Hide when unavailable',
                    name: 'hideWhenUnavailable',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket when it is unavailable',
                  },
                  {
                    label: 'Hide when sold out',
                    name: 'hideWhenSoldOut',
                    type: 'checkbox',
                    tooltip: 'Hide this ticket when it is sold out',
                  },
                ],
                reduceMargin: true,
              },
              {
                type: 'disclosure',
                label: 'Additional settings',
                fields: [
                  {
                    type: 'columns',
                    columns: 4,
                    visible: ticketGroup.ticketSelectionType === 'quantity',
                    gap: 'sm',
                    fields: [
                      {
                        name: 'minQuantity',
                        label: 'Min',
                        tooltip: 'Minimum number of tickets that can be selected',
                        type: 'number',
                        min: 0,
                        max: 999,
                        reduceMargin: true,
                      },
                      {
                        name: 'maxQuantity',
                        label: 'Max',
                        tooltip: 'Maximum number of tickets that can be selected',
                        type: 'number',
                        min: 1,
                        max: 999,
                        reduceMargin: true,
                      },
                    ],
                    noMargin: true,
                  },
                  {
                    type: 'fieldgroup',
                    label: 'Show on ticket',
                    fields: [
                      {
                        name: 'showAvailableQuantity',
                        label: 'Quantity available',
                        type: 'checkbox',
                      },
                      {
                        name: 'showTicketSaleDates',
                        label: 'Sale dates',
                        type: 'checkbox',
                      },
                    ],
                    reduceMargin: true,
                  },
                  {
                    name: 'reference',
                    label: 'Reference',
                    type: 'text',
                    tooltip:
                      'Used to identify this ticket in Zapier, webhooks, and query parameters.',
                    noMargin: true,
                  },
                ],
              },
            ],
          },
        ];
      }}
      formSubmit={
        ticketType?._id
          ? null
          : () => {
              if (!ticketType?._id) {
                return (
                  <FormSubmit>
                    <div className="flex flex-row justify-between w-full items-center">
                      <div>
                        {(ticketGroups.length > 1 || ticketGroup?.ticketTypes?.length > 0) && (
                          <FormSubmit.Cancel
                            appearance="secondary"
                            onClick={onCancelEditTicketType}
                          >
                            Cancel
                          </FormSubmit.Cancel>
                        )}
                      </div>

                      <div className="flex flex-row gap-x-2">
                        <FormSubmit.Button
                          appearance="secondary"
                          onClick={() => {
                            setShouldAddAnother(true);
                          }}
                        >
                          + Add another
                        </FormSubmit.Button>
                        <FormSubmit.Button>Save</FormSubmit.Button>
                      </div>
                    </div>
                  </FormSubmit>
                );
              }
            }
      }
      formSubmitDirection={formSubmitDirection}
      formSubmitAlign="end"
      cancelButton={
        cancelButton
          ? cancelButton
          : {
              label: 'Cancel',
              onClick: onCancelEditTicketType,
            }
      }
      submitButton={submitButton}
      showState
    />
  );
}
